import Accordions from "@/common/components/Accordions";
import Checkbox from "@/common/components/Checkbox";
import MaskedTextField from "@/common/components/MaskedTextField";
import { documentsFieldSettings } from "@/common/constants/documents.constant";
import { getFilenameFromUrl } from "@/common/utils/getFilenameFromUrl";
import { RequestedReview } from "@/features/establishmentLeadsFunnel/components/RequestedReview";
import { ReviewButtons } from "@/features/establishmentLeadsFunnel/components/ReviewButtons";
import { PrivateCompanyLeadsFunnelUpdateContactValidationSchema } from "@/features/privateCompanyLeadsFunnel/schemas/update.schema";
import { ContactData, ContactState } from "@/features/privateCompanyLeadsFunnel/types/privateCompanyLead";
import { ContactDocument } from "@/features/publicCompanyLeadsFunnel/components/ContactDocument";
import { useIsMutating, UseMutateFunction } from "@tanstack/react-query";
import { AxiosResponse } from "axios";
import { CircleAlertIcon, ThumbsDown, ThumbsUp } from "lucide-react";
import { ChangeEvent, Dispatch, SetStateAction } from "react";
import { Control, Controller, FieldErrors, UseFormSetValue, UseFormWatch } from "react-hook-form";

interface ContactDocumentsSectionProps {
  contact?: ContactData;
  control: Control<PrivateCompanyLeadsFunnelUpdateContactValidationSchema>;
  watch: UseFormWatch<PrivateCompanyLeadsFunnelUpdateContactValidationSchema>;
  setValue?: UseFormSetValue<PrivateCompanyLeadsFunnelUpdateContactValidationSchema>;
  errors: FieldErrors<PrivateCompanyLeadsFunnelUpdateContactValidationSchema>;
  files: { id: string, file: Blob, fileName: string; }[];
  setFiles: Dispatch<SetStateAction<{ id: string, file: Blob, fileName: string; }[]>>;
  setDocumentToModal: Dispatch<SetStateAction<string>>;
  showReviewButtons?: boolean;
  approveContactDocument: UseMutateFunction<any, Error, { description?: string; } | undefined, unknown>;
  declineContactDocument: UseMutateFunction<any, Error, { description?: string; } | undefined, unknown>;
  toggleDocument: UseMutateFunction<AxiosResponse, Error, { value: boolean, type: string, documentId?: string; }, unknown>;
  editContact: boolean;
  isInRegistered: boolean;
  updateContactIsPending: boolean;
}

export const ContactDocumentsSection = ({
  contact,
  control,
  watch,
  setValue,
  errors,
  files,
  setFiles,
  setDocumentToModal,
  showReviewButtons,
  approveContactDocument,
  declineContactDocument,
  toggleDocument,
  editContact,
  isInRegistered,
  updateContactIsPending,
}: ContactDocumentsSectionProps) => {
  const isMutating = useIsMutating();

  const docsLength = watch('contacts.documents')?.filter((doc) => editContact || doc?.checked).length;

  return (
    <div>
      <p className="text-lg font-bold">Documentos recebidos</p>
      <p className="font-bold mt-3">Pessoais</p>

      <div className="mt-2 flex flex-col gap-2">
        {docsLength > 0 ? (
          <Accordions
            items={watch('contacts.documents')?.filter((doc) => editContact || doc?.checked)?.map((formDocument, index) => {
              const contactDocument = contact?.documents?.find((doc) => doc?.type === formDocument?.type);

              const declineDocument = (data?: { description?: string; }) => {
                setDocumentToModal(formDocument?.id || '');
                declineContactDocument(data);
              };

              return {
                index,
                hide: contact?.state !== ContactState.pending && !formDocument?.id,
                disabled: !formDocument?.id || !formDocument.checked,
                id: contactDocument?.id,
                dataTestid: `accordion_${formDocument?.doc_options?.name}_trigger`,
                head: (
                  <>
                    {editContact ? (
                      <Checkbox
                        label={formDocument?.doc_options?.display_name || ''}
                        name={formDocument?.doc_options?.name}
                        disabled={!editContact || updateContactIsPending || !!isMutating || isInRegistered}
                        checked={formDocument?.checked || false}
                        onClick={(event) => event.stopPropagation()}
                        onChange={(event: ChangeEvent<HTMLInputElement>) => {
                          toggleDocument({
                            value: event.target.checked,
                            type: formDocument?.type || '',
                            documentId: formDocument?.id || '',
                          })
                        }}
                      />
                    ) : (
                      <div className="flex gap-[10px]">
                        {formDocument?.last_review?.state === 'approved' && (
                          <ThumbsUp className="fill-success" strokeWidth={1} />
                        )}
                        {formDocument?.last_review?.state === 'disapproved' && (
                          <ThumbsDown className="fill-warning" strokeWidth={1} />
                        )}
                        <p>{formDocument?.doc_options?.display_name}</p>
                      </div>
                    )}
                  </>
                ),
                body: (
                  <div className="flex flex-col gap-4">
                    {formDocument?.doc_options?.attributes.map((attribute) => {
                      const attributeName = Object.keys(attribute).at(0) as keyof typeof contactDocument;

                      const documentSetting =
                        attributeName in documentsFieldSettings
                          ? documentsFieldSettings[attributeName]
                          : documentsFieldSettings.default(attributeName);

                      return (
                        <Controller
                          control={control}
                          name={`contacts.documents.${index}.${attributeName}` as const}
                          key={`contacts.documents.${index}.${attributeName}`}
                          render={({ field }) => (
                            <MaskedTextField
                              key={`${formDocument?.doc_options?.name}.${attributeName}`}
                              label={documentSetting.label}
                              mask={documentSetting.mask}
                              dataTestid={`${formDocument?.doc_options?.name}_${attributeName}_input`}
                              markAsRequired
                              defaultValue={
                                formDocument?.[attributeName]
                                  ? documentSetting.defaultValueTransformer(formDocument?.[attributeName])
                                  : ''
                              }
                              {...field}
                              errorMessage={(errors?.contacts?.documents?.[attributeName] as any)?.message || ' '}
                              disabled={!editContact || updateContactIsPending || !contactDocument?.id}
                            />
                          )}
                        />
                      );
                    })}

                    <div>
                      <ContactDocument
                        contactDocument={contactDocument}
                        document={formDocument?.doc_options!}
                        disabled={!editContact || updateContactIsPending || !contactDocument?.id}
                        fileName={
                          files?.find((file) => file.id === contactDocument?.id)
                            ? files.find((file) => file.id === contactDocument?.id)?.fileName
                            : contactDocument?.file?.url
                              ? getFilenameFromUrl(contactDocument?.file?.url!)
                              : undefined
                        }
                        currentFileUrl={
                          files.find((file) => file.id === contactDocument?.id)
                            ? URL.createObjectURL(files.find((file) => file.id === contactDocument?.id)?.file!)
                            : contactDocument?.file?.url
                              ? contactDocument?.file?.url
                              : undefined
                        }
                        setFiles={setFiles}
                        onChange={(file) => setValue?.(`contacts.documents.${index}.file`, file)}
                        dataTestid={`${formDocument?.doc_options?.name}_file_input`}
                      />
                    </div>

                    {showReviewButtons && (
                      <ReviewButtons
                        reviewState={formDocument?.last_review?.state || ''}
                        onApprove={() => {
                          setDocumentToModal(formDocument?.id || '');
                          approveContactDocument({});
                        }}
                        onDecline={(data) => declineDocument(data)}
                      />
                    )}

                    {formDocument?.last_review?.state === 'disapproved' && (
                      <RequestedReview description={formDocument?.last_review.description || ''} />
                    )}
                  </div>
                ),
              };
            })}
          />
        ) : (
          <div className="flex items-center gap-2 py-4">
            <CircleAlertIcon strokeWidth={1} color="var(--warning)" />

            <div>
              <p className="font-light italic">
                Selecione os documentos na tela de edição
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
